import React from 'react';
import { useTranslation } from 'react-i18next';
import type { StatisticsSummaryView } from '../../api/generated/models/StatisticsSummaryView';
import { getDisplayCurrency, getAmountForCurrency, formatMoney } from '../../utils/statisticsHelpers';

const GROUP_COLORS = ['#2563EB', '#059669', '#D97706', '#DC2626', '#7C3AED'];

interface SalesByCustomerGroupWidgetProps {
  data: StatisticsSummaryView | null;
}

/**
 * ТЗ п.2: Horizontal bars for Today / Yesterday / Day before yesterday.
 * Each bar shows stacked segments per customer group with amounts.
 * Data source: salesByCustomerGroupByDay (last 3 days from the array).
 */
export const SalesByCustomerGroupWidget: React.FC<SalesByCustomerGroupWidgetProps> = ({ data }) => {
  const { t } = useTranslation();
  const currency = getDisplayCurrency(data);

  // Get the last 3 day entries sorted desc (newest first)
  const dayEntries = React.useMemo(() => {
    const raw: any[] = data?.salesByCustomerGroupByDay ?? [];
    const sorted = [...raw].sort((a, b) => b.date.localeCompare(a.date));
    return sorted.slice(0, 3);
  }, [data]);

  // Collect all unique group names across all days for the legend
  const allGroups = React.useMemo(() => {
    const groupMap = new Map<number, string>();
    dayEntries.forEach((day) => {
      (day.groups ?? []).forEach((g: any) => {
        if (!groupMap.has(g.customerGroupId)) {
          groupMap.set(g.customerGroupId, g.customerGroupName);
        }
      });
    });
    // Also check aggregate if no daily data
    if (groupMap.size === 0) {
      (data?.salesByCustomerGroup ?? []).forEach((g: any) => {
        groupMap.set(g.customerGroupId, g.customerGroupName);
      });
    }
    return Array.from(groupMap.entries());
  }, [dayEntries, data]);

  const dayLabels = [
    t('dashboard_stats_today'),
    t('dashboard_stats_yesterday'),
    t('dashboard_stats_day_before'),
  ];

  // Compute the max total across all 3 days for proportional bar widths
  const maxTotal = React.useMemo(() => {
    let max = 0;
    dayEntries.forEach((day) => {
      const total = (day.groups ?? []).reduce((s: number, g: any) => s + getAmountForCurrency(g.amountByCurrency, currency), 0);
      if (total > max) max = total;
    });
    return max;
  }, [dayEntries, currency]);

  return (
    <div className="stat-card-half">
      <div className="stat-card-header">
        <h3 className="stat-card-title">{t('dashboard_stats_sales_by_customer_group')}</h3>
      </div>

      {/* Legend */}
      {allGroups.length > 0 && (
        <div className="customer-group-legend">
          {allGroups.map(([id, name], i) => (
            <span key={id} className="customer-group-legend-item">
              <span
                className={`customer-group-legend-dot customer-group-bar-fill--${i % GROUP_COLORS.length}`}
              />
              {name}
            </span>
          ))}
        </div>
      )}

      {/* 3-day bars */}
      <div className="customer-group-days">
        {[0, 1, 2].map((idx) => {
          const day = dayEntries[idx];
          const groups: any[] = day?.groups ?? [];
          const dayTotal = groups.reduce((s: number, g: any) => s + getAmountForCurrency(g.amountByCurrency, currency), 0);

          return (
            <div key={idx} className="customer-group-day-row">
              <div className="customer-group-day-label">{dayLabels[idx]}</div>
              <div className="customer-group-day-bar-wrapper">
                <div className="customer-group-bar-track">
                  {groups.map((g: any, gi: number) => {
                    const amount = getAmountForCurrency(g.amountByCurrency, currency);
                    const pct = maxTotal > 0 ? (amount / maxTotal) * 100 : 0;
                    const colorIdx = allGroups.findIndex(([id]) => id === g.customerGroupId);
                    return (
                      <div
                        key={g.customerGroupId}
                        className={`customer-group-bar-fill customer-group-bar-fill--${(colorIdx >= 0 ? colorIdx : gi) % GROUP_COLORS.length}`}
                        style={{ '--bar-pct': `${pct}%` } as React.CSSProperties}
                        title={`${g.customerGroupName}: ${formatMoney(amount, currency)}`}
                      />
                    );
                  })}
                </div>
                <div className="customer-group-day-amounts">
                  {groups.length > 0 ? (
                    groups.map((g: any) => (
                      <span key={g.customerGroupId} className="customer-group-amount">
                        {formatMoney(getAmountForCurrency(g.amountByCurrency, currency), currency)}
                      </span>
                    ))
                  ) : (
                    <span className="customer-group-amount">{formatMoney(0, currency)}</span>
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};
